import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { BsShop } from "react-icons/bs";
import { AiFillStar } from "react-icons/ai";
import { backend_url, server } from "../../server";
import SocialProfileBadges from "../shared/SocialProfileBadges";

const primaryButtonClasses =
  "inline-flex h-10 items-center justify-center rounded-xl bg-[#38513b] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2f4232]";
const secondaryButtonClasses =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#38513b] hover:text-[#38513b]";

const hasText = (value) =>
  typeof value === "string" && value.replace(/<[^>]*>/g, "").trim().length > 0;

const getShopDescription = (shop) => {
  const raw = shop?.description || shop?.shopDescription || "";
  if (!hasText(raw)) return "";
  return raw.replace(/<[^>]*>/g, "").trim();
};

const getLocationLabel = (shop) => {
  const address = shop?.address || shop?.shopAddress || "";
  if (hasText(address)) return address.trim();
  const city = shop?.city?.trim();
  const country = shop?.country?.trim();
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  if (hasText(shop?.postCode)) return shop.postCode.trim();
  return "";
};

const ShopInfo = ({ isOwner, shop }) => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const showPrivateDetails = Boolean(isOwner);

  const activeShop = shop || user;

  const coverImage = activeShop?.coverImage
    ? `${backend_url}/${activeShop.coverImage}`
    : null;
  const avatar = activeShop?.avatar
    ? `${backend_url}/${activeShop.avatar}`
    : null;
  const createdAt = activeShop?.createdAt
    ? new Date(activeShop.createdAt).toLocaleDateString()
    : null;
  const description = getShopDescription(activeShop);
  const locationLabel = getLocationLabel(activeShop);
  const ratingValue =
    activeShop?.averageRating ??
    activeShop?.averageRatingFromCustomers ??
    activeShop?.ratings ??
    activeShop?.rating ??
    activeShop?.avgRating ??
    0;
  const totalReviews =
    activeShop?.totalReviews ??
    activeShop?.totalReviewsFromCustomers ??
    activeShop?.reviews?.length ??
    0;

  const renderStars = () => {
    const filled = Math.round(ratingValue);
    return (
      <span className="flex items-center gap-1 text-amber-400">
        {[1, 2, 3, 4, 5].map((value) => (
          <AiFillStar
            key={value}
            size={16}
            className={value <= filled ? "text-amber-400" : "text-slate-300"}
          />
        ))}
        <span className="text-sm font-medium text-slate-600">
          {ratingValue?.toFixed ? ratingValue.toFixed(1) : ratingValue}
          <span className="text-xs text-slate-400">
            {" "}
            ({totalReviews} review{totalReviews === 1 ? "" : "s"})
          </span>
        </span>
      </span>
    );
  };

  const handleContactSeller = useCallback(async () => {
    if (!activeShop?._id) {
      toast.error("Seller information is unavailable.");
      return;
    }

    if (!user?._id) {
      toast.error("Please sign in to contact the seller.");
      navigate("/login");
      return;
    }

    // Prevent users from messaging themselves
    if (user._id && activeShop._id && String(user._id) === String(activeShop._id)) {
      toast.error("You cannot message yourself");
      return;
    }

    const groupTitle = `${activeShop._id}_${user._id}`;

    try {
      const response = await axios.post(
        `${server}/conversation/create-new-conversation`,
        {
          groupTitle,
          userId: user._id,
          sellerId: activeShop._id,
        },
        { withCredentials: true }
      );

      const conversationId =
        response.data?.existingConversation?._id ||
        response.data?.conversation?._id ||
        response.data?._id;

      if (!conversationId) {
        throw new Error("Unable to determine conversation");
      }

      navigate(`/inbox?conversation=${conversationId}`);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "We couldn't start the conversation. Please try again."
      );
    }
  }, [activeShop?._id, navigate, user?._id]);

  if (!activeShop) {
    return (
      <aside className="space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex h-24 items-center justify-center bg-[#38513b]/5 text-[#38513b]">
            <BsShop size={28} />
          </div>
          <div className="mt-6 space-y-4">
            <div className="h-4 w-40 rounded bg-slate-200" />
            <div className="h-3 w-28 rounded bg-slate-200" />
            <div className="h-3 w-24 rounded bg-slate-200" />
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="space-y-6">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        {/* {coverImage ? (
          <div className="h-24 bg-slate-100">
            <img
              src={coverImage}
              alt="Seller cover"
              className="h-full w-full object-contain p-3"
            />
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center bg-[#38513b]/5 text-[#38513b]">
            <BsShop size={28} />
          </div>
        )} */}

        <div className="p-6">
          <div className="gap-4">
            <div className="h-20 w-20 overflow-hidden rounded-2xl border-4 border-white shadow-md">
              {avatar ? (
                <img
                  src={avatar}
                  alt={activeShop?.name || "Seller avatar"}
                  className="h-full w-full object-contain p-3"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[#38513b]/10 text-lg font-semibold text-[#38513b]">
                  {activeShop?.name
                    ? activeShop.name
                        .split(" ")
                        .map((word) => word[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()
                    : "SH"}
                </div>
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-900 py-2">
                {activeShop?.name || "Seller name unavailable"}
              </h1>
              {showPrivateDetails && description ? (
                <p className="text-sm text-slate-500">{description}</p>
              ) : null}
            </div>
          </div>

          <div className="mt-6 space-y-4 text-sm text-slate-600 shop-inn">
            {totalReviews > 0 ? (
              <div className="flex items-center gap-2">{renderStars()}</div>
            ) : null}
            <SocialProfileBadges
              profiles={activeShop?.linkedSocialProfiles}
              compact
              className="pt-1"
            />
            {showPrivateDetails && locationLabel ? (
              <div className="flex items-center gap-2">
                <span>{locationLabel}</span>
              </div>
            ) : null}
            {showPrivateDetails && createdAt ? (
              <div className="flex items-center gap-2">
                <span>Joined on {createdAt}</span>
              </div>
            ) : null}
            {showPrivateDetails && activeShop?.email ? (
              <div className="flex items-center gap-2 break-all">
                <a
                  href={`mailto:${activeShop.email}`}
                  className="text-sm font-medium text-[#38513b] hover:underline"
                >
                  {activeShop.email}
                </a>
              </div>
            ) : null}
            {showPrivateDetails && activeShop?.phoneNumber ? (
              <div className="flex items-center gap-2">
                <span>{activeShop.phoneNumber}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {!isOwner ? (
              <button
                type="button"
                onClick={handleContactSeller}
                className={primaryButtonClasses}
              >
                Message seller
              </button>
            ) : null}
            
            {isOwner && (
              <Link
                to="/profile/dashboard"
                className={`${secondaryButtonClasses} justify-center`}
              >
                Manage seller
              </Link>
            )}
          </div>
        </div>
      </div>

      {showPrivateDetails && activeShop?.shopTagline && (
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Seller tagline
          </h2>
          <p className="mt-2 text-sm text-slate-700">
            {activeShop.shopTagline}
          </p>
        </div>
      )}
    </aside>
  );
};

export default ShopInfo;
